// Contenido Teórico Especializado - Inglés Diagnóstico B1 EXANI-II (Ceneval 2026)
// Nivel B1 según el Marco Común Europeo de Referencia para las Lenguas (MCER).

export const INGLES_THEORY = {
  ing_401: {
    title: "Present Simple vs Present Continuous",
    intro: "El examen evalúa la diferencia fundamental entre hábitos permanentes o hechos generales (Present Simple) y acciones que ocurren en el momento del habla o situaciones temporales (Present Continuous).",
    sections: [
      {
        subtitle: "1. Present Simple (Habits, Routines, Facts)",
        content: `- **Uso**: Rutinas diarias, verdades científicas y leyes universales.
- **Regla de 3ra Persona Singular (He, She, It)**: Se agrega *-s*, *-es* o *-ies* al verbo:
  - *He works in a hospital.*
  - *She watches documentaries.*
  - *It flies high.*
- **Auxiliares en Negación y Pregunta**: Do / Does:
  - *He doesn't eat meat.* | *Do you live here?*
- **Adverbios de frecuencia**: *always, usually, often, sometimes, rarely, never* (se colocan antes del verbo principal, pero después del verbo to be).`
      },
      {
        subtitle: "2. Present Continuous (Right Now, Temporary)",
        content: `- **Estructura**: Subject + [am / is / are] + Verb-ING.
- **Uso**: Acciones en progreso al hablar o tendencias actuales.
  - *Listen! The professor is explaining the theorem.*
  - *I am studying for the EXANI-II exam this week.*
- **Time expressions**: *now, right now, at the moment, currently, this semester.*`
      },
      {
        subtitle: "3. Stative Verbs (¡No se usan en continuo!)",
        content: `Verbos de estado mental, emoción o posesión NO suelen llevar -ing:
*know, believe, understand, remember, love, hate, want, prefer, own, belong*.
- *Correct*: I understand the question.
- *Incorrect*: I am understanding the question.`
      }
    ],
    examples: [
      {
        problem: "Choose the correct sentence: 'Water _______ at 100 degrees Celsius, but the water in that pot _______ right now.'",
        solution: `'boils / is boiling'. (El primer hecho es una ley científica universal en Present Simple; el segundo ocurre en este momento con Present Continuous).`
      }
    ],
    exam_tips: "Si ves 'at the moment' o 'look!', busca opciones con 'am/is/are + verb-ing'. Si ves 'every day' o 'usually', busca el verbo en Present Simple con atención a la tercera persona (-s)."
  },

  ing_402: {
    title: "Past Simple, Past Continuous & Regular/Irregular Verbs",
    intro: "Comprende la narración de hechos terminados en el pasado y la interrupción de acciones en progreso mediante 'when' y 'while'.",
    sections: [
      {
        subtitle: "1. Past Simple (Completed Actions)",
        content: `- **Verbos regulares**: Terminan en *-ed* (*studied, played, lived*).
- **Verbos irregulares comunes**: *went, took, saw, wrote, bought, thought, had*.
- **Auxiliar**: DID para preguntas y DIDN'T para negaciones (el verbo vuelve a su forma base):
  - *She didn't write the report.* (No *didn't wrote*).`
      },
      {
        subtitle: "2. Past Continuous (Background Action in Progress)",
        content: `- **Estructura**: Subject + [was / were] + Verb-ING.
- **La Fórmula Clásica de Interrupción**:
  $$\\text{Past Continuous (Acción Larga)} + \\textbf{WHEN} + \\text{Past Simple (Interrupción)}$$
  $$\\textbf{WHILE} + \\text{Past Continuous} + \\text{Past Simple}$$
  - *I was reading the textbook when the power went out.*
  - *While we were doing the experiment, the alarm rang.*`
      }
    ],
    examples: [
      {
        problem: "Complete: 'The students _______ the simulation when the bell _______.'",
        solution: `'were taking / rang'. (La acción en progreso 'were taking' fue interrumpida por la puntual 'rang').`
      }
    ],
    exam_tips: "Cuidado: después de 'didn't', el verbo SIEMPRE va en infinitivo/forma base. 'Did you went?' es un distractor típico de Ceneval."
  },

  ing_403: {
    title: "Present Perfect & Time Expressions (Since, For, Already, Yet)",
    intro: "Conecta experiencias del pasado con el presente. Es uno de los tiempos verbales con mayor peso diagnóstico en el nivel B1.",
    sections: [
      {
        subtitle: "1. Estructura y Uso",
        content: `- **Estructura**: Subject + [have / has] + Past Participle (3ra columna: *done, seen, written*).
- **Uso**: Experiencias de vida (sin especificar fecha) o acciones que iniciaron en el pasado y continúan hoy.`
      },
      {
        subtitle: "2. Marcadores Temporales Clave",
        content: `- **Since**: Señala el punto de partida en el tiempo (*since 2020, since yesterday, since Monday*).
- **For**: Señala la duración del periodo (*for 3 years, for two hours, for a long time*).
- **Already**: 'Ya' en oraciones afirmativas (*I have already finished the quiz*).
- **Yet**: 'Aún/Todavía' al final de oraciones negativas o preguntas (*Have you submitted the assignment yet? / No, not yet*).`
      }
    ],
    examples: [
      {
        problem: "Choose the correct preposition: 'She has lived in Guadalajara _______ five years, but she has worked at this hospital _______ 2022.'",
        solution: `'for / since'. (Five years es duración $\\rightarrow$ for; 2022 es punto de partida $\\rightarrow$ since).`
      }
    ],
    exam_tips: "Si la oración menciona un año exacto en el pasado (ej. 'in 2018'), se usa Past Simple (lived). Si usa 'since 2018', se usa Present Perfect (has lived)."
  },

  ing_404: {
    title: "Future Forms: Will, Be Going To & Present Continuous for Future",
    intro: "Evalúa la intención y certeza en las proyecciones futuras (predicciones, decisiones espontáneas y planes confirmados).",
    sections: [
      {
        subtitle: "1. Comparativa de Formas Futuras",
        content: `- **Will**: Decisiones espontáneas en el momento de hablar, promesas o predicciones basadas en opinión personal:
  - *I think it will rain tomorrow.* | *I will help you with that problem!*
- **Be going to**: Intenciones o planes previos, o predicciones basadas en evidencia visual presente:
  - *Look at those dark clouds! It is going to rain.*
  - *She is going to study medicine next semester.*
- **Present Continuous para el futuro**: Acuerdos con fecha y hora fijadas con otra persona:
  - *I am meeting the academic advisor on Friday at 10:00 AM.*`
      }
    ],
    examples: [
      {
        problem: "Which option expresses an immediate decision made at the moment of speaking?",
        solution: `'The phone is ringing. I will answer it.'`
      }
    ],
    exam_tips: "Si hay evidencia visual presente ('Look at that car moving fast!'), la opción correcta es 'is going to crash', no 'will crash'."
  },

  ing_405: {
    title: "Modals of Ability, Obligation & Advice (Can, Must, Should)",
    intro: "Los verbos modales expresan la actitud del hablante hacia la acción (capacidad, necesidad, deber moral o sugerencia).",
    sections: [
      {
        subtitle: "1. Tabla de Modales B1",
        content: `- **Can / Could**: Habilidad presente o pasada (*I can solve this equation / When I was younger, I could run faster*).
- **Should / Shouldn't**: Consejo o recomendación (*You should review grammar before the exam*).
- **Must / Have to**: Obligación estricta o ley (*You must wear a helmet / Students have to arrive on time*).
- **Mustn't**: Prohibición terminante (*You mustn't use phones during the test*).
- **Don't have to**: Falta de obligación (es opcional, no prohibido: *Tomorrow is Sunday, you don't have to wake up early*).`
      }
    ],
    examples: [
      {
        problem: "In an exam instructions sheet: 'Candidates _______ consult books or electronic devices during the session.'",
        solution: `'must not' (prohibición estricta).`
      }
    ],
    exam_tips: "Cuidado: 'mustn't' significa prohibido; 'don't have to' significa que no es necesario pero puedes hacerlo si quieres."
  },

  ing_406: {
    title: "Conditionals: Zero, First and Second Conditionals",
    intro: "Pregunta clásica en el examen diagnóstico B1 para evaluar relaciones causa-consecuencia y situaciones hipotéticas.",
    sections: [
      {
        subtitle: "1. Reglas Estructurales",
        content: `- **Zero Conditional (Verdades universales)**:
  $$\\text{If} + \\text{Present Simple}, \\quad \\text{Present Simple}$$
  *If you heat water, it boils.*
- **First Conditional (Probabilidad real futura)**:
  $$\\text{If} + \\text{Present Simple}, \\quad \\text{WILL} + \\text{Base Verb}$$
  *If you study hard, you will pass the EXANI-II.*
- **Second Conditional (Situaciones hipotéticas o irreales en el presente)**:
  $$\\text{If} + \\text{Past Simple}, \\quad \\text{WOULD} + \\text{Base Verb}$$
  *If I had more time, I would learn German.* (Nota con to be: *If I were you, I would...*)`
      }
    ],
    examples: [
      {
        problem: "Complete: 'If scientists _______ a cure, millions of lives _______ saved.' (Hypothetical)",
        solution: `'found / would be' (Second conditional).`
      }
    ],
    exam_tips: "Nunca coloques 'will' o 'would' dentro de la cláusula con 'if'. *'If I will study'* es 100% incorrecto; debe ser *'If I study'*."
  },

  ing_407: {
    title: "Passive Voice in Present & Past",
    intro: "Muy utilizada en textos académicos y científicos donde el objeto de la acción o el experimento es más relevante que quien lo realizó.",
    sections: [
      {
        subtitle: "1. Construcción de la Voz Pasiva",
        content: `$$\\text{Objeto Activo} + [\\text{be en el tiempo correcto}] + \\text{Past Participle} + (\\text{by} + \\text{Agente})$$
- **Presente**: am / is / are + participio:
  - *Active*: Researchers publish many papers.
  - *Passive*: Many papers **are published** by researchers.
- **Pasado**: was / were + participio:
  - *Active*: Alexander Fleming discovered penicillin in 1928.
  - *Passive*: Penicillin **was discovered** in 1928 by Alexander Fleming.`
      }
    ],
    examples: [
      {
        problem: "Choose the passive form: 'The committee approved the new academic guidelines.'",
        solution: `'The new academic guidelines were approved by the committee.'`
      }
    ],
    exam_tips: "El verbo 'to be' siempre debe concordar en número con el nuevo sujeto paciente: guidelines (plural) $\\rightarrow$ *were approved*."
  },

  ing_408: {
    title: "Reading Comprehension in English & Academic Vocabulary B1",
    intro: "Los últimos 15 reactivos de la sección diagnóstica corresponden a lectura en inglés: extraer información específica, entender el vocabulario en contexto y determinar el propósito del texto.",
    sections: [
      {
        subtitle: "1. Técnicas de Lectura: Skimming & Scanning",
        content: `- **Skimming**: Lectura rápida para obtener la idea global en 30 segundos.
- **Scanning**: Búsqueda visual de palabras clave específicas (nombres, números, fechas o términos técnicos) sin leer todo el párrafo.`
      },
      {
        subtitle: "2. Conectores en Inglés Indispensables",
        content: `- Contrast: *However, Although, Despite, On the other hand.*
- Cause & Effect: *Because of, Therefore, As a result, Consequently.*
- Addition: *Furthermore, In addition, Moreover.*`
      }
    ],
    examples: [
      {
        problem: "What does the word 'breakthrough' most likely mean in a medical article?",
        solution: `'A significant discovery or advance' (un avance o hito científico trascendental).`
      }
    ],
    exam_tips: "Aunque la sección de inglés es diagnóstica y no resta puntaje en la mayoría de universidades, un puntaje alto en B1 suele ser requisito para exentar materias de idiomas en la carrera."
  }
};
